import { useCallback, useEffect, useRef } from 'react'
import { useWorkspaceSession } from '@/hooks/useWorkspaceSession'
import { useOrganizationStore } from '@/store/organization.store'
import { useQueryClient } from '@tanstack/react-query'

import { subscribeProjectInvalidation } from '../project-query-invalidation'

/** Never apply another writer's project snapshot to a reader's cache. */
export function useProjectInvalidation(includeLists: boolean) {
  const client = useQueryClient()
  const scope = useWorkspaceSession()
  const organizationId = useOrganizationStore(
    state => state.currentOrganization?.id,
  )
  const subscription = useRef<ReturnType<
    typeof subscribeProjectInvalidation
  > | null>(null)
  useEffect(() => {
    if (!scope.enabled) return
    const current = subscribeProjectInvalidation(client, scope)
    subscription.current = current
    return () => {
      current.dispose()
      subscription.current = null
    }
  }, [client, scope])
  return useCallback(
    (data: {
      id?: unknown
      organizationId?: unknown
      eventId?: unknown
      _eventId?: unknown
      _bulk?: unknown
    }) => {
      if (!scope.enabled || scope.signal.aborted) return
      // An import announces its whole batch once, with no record id: refresh
      // the lists (and any open records) in one pass.
      if (data._bulk === true) {
        if (data.organizationId && data.organizationId !== organizationId)
          return
        if (includeLists) subscription.current?.refreshAll()
        return
      }
      if (typeof data.id !== 'string') return
      if (data.organizationId && data.organizationId !== organizationId) return
      const eventId = data.eventId ?? data._eventId
      subscription.current?.notify(
        data.id,
        includeLists,
        typeof eventId === 'string' ? eventId : undefined,
      )
    },
    [scope.enabled, scope.signal, organizationId, includeLists],
  )
}
